import {
  DSCardWrapper,
  DSDropdown,
  DSDropdownOption,
} from "@components/design-system/molecules";
import {
  PIMS_EXPORT_FORMAT_DEFAULT,
  VALID_FORMATS,
  type PimsExportFormat,
  usePimsExportFormat,
  useSetPimsExportFormat,
} from "@hooks/use-pims-export-format";
import mixpanel from "mixpanel-browser";

import { alert } from "../common/Alert";

type PimsExportFormatOption = DSDropdownOption & {
  value: PimsExportFormat;
};

const PIMS_EXPORT_FORMAT_OPTIONS: PimsExportFormatOption[] = [
  { label: "Plain text (no formatting)", value: "plain_text" },
  { label: "Markdown symbols", value: "markdown" },
  { label: "Rich text", value: "rich_text" },
];

export default function PimsExportFormatSetting() {
  const pimsExportFormat = usePimsExportFormat();
  const { setFormat, loading } = useSetPimsExportFormat();

  const handleChange = async (value: PimsExportFormat) => {
    if (!VALID_FORMATS.includes(value)) return;
    try {
      const updated = await setFormat(value);
      if (!updated) return;
      alert("success", "PIMS export format updated successfully.");
      mixpanel.track("Updated Account Setting", {
        settingName: "PIMS Export Format",
        settingKey: "user.pimsExportFormat",
        settingValue: value,
      });
    } catch {
      alert(
        "error",
        "Failed to save PIMS export format. Please try again.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-sm">
      <div className="my-sm">
        <DSDropdown<PimsExportFormatOption>
          options={PIMS_EXPORT_FORMAT_OPTIONS}
          value={pimsExportFormat || PIMS_EXPORT_FORMAT_DEFAULT}
          onChange={(_, option) => handleChange(option.value)}
          disabled={loading}
          dataTestId="pims-export-format-dropdown"
        />
      </div>
      <div className="mx-2 text-xs text-secondary space-y-md">
        <DSCardWrapper
          dataTestId="pims-copy-plain-text"
          variant={
            pimsExportFormat === "plain_text" ? "success" : "default"
          }
          interactive={true}
          onClick={() => {
            handleChange("plain_text");
          }}
        >
          <strong className="text-primary">
            Plain text (no formatting)
          </strong>{" "}
          — Removes all formatting symbols. Best for PIMS that do not
          support any text formatting (e.g. AVImark, Instinct).
          <div className="mt-xs ml-sm text-2xs italic text-placeholder">
            Example: <br />
            <br /> Subjective <br /> Patient is a 5 year old MN
            Labrador presenting for vomiting...
          </div>
        </DSCardWrapper>
        <DSCardWrapper
          dataTestId="pims-copy-markdown"
          variant={
            pimsExportFormat === "markdown" ? "success" : "default"
          }
          interactive={true}
          onClick={() => {
            handleChange("markdown");
          }}
        >
          <strong className="text-primary">Markdown symbols</strong> —
          Uses symbols like ** for bold and - for lists. This is the
          default behaviour - if you're not sure what to choose, pick
          this option.
          <div className="mt-xs ml-sm text-2xs italic text-placeholder">
            Example:
            <br />
            <br />
            **Subjective**
            <br />- Patient is a 5 year old MN Labrador presenting for
            vomiting...
          </div>
        </DSCardWrapper>
        <DSCardWrapper
          dataTestId="pims-copy-rich-text"
          variant={
            pimsExportFormat === "rich_text" ? "success" : "default"
          }
          interactive={true}
          onClick={() => {
            handleChange("rich_text");
          }}
        >
          <strong className="text-primary">Rich text</strong> —
          Preserves bold, italic, lists, and other formatting. Best
          for PIMS that support formatted/rich text pasting (e.g.
          Pulse/eVetPractice, ezyVet (new clinical experience),
          Digitail, Cornerstone).
          <div className="mt-xs ml-sm text-2xs italic text-placeholder">
            Example: <br />
            <br />
            <b>Subjective</b>
            <br />• Patient is a 5 year old MN Labrador presenting for
            vomiting...
          </div>
        </DSCardWrapper>
      </div>
    </div>
  );
}
